import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import Swal from "sweetalert2";
import { FaCreditCard, FaMoneyBillWave, FaReceipt, FaUndoAlt } from "react-icons/fa";
import POSNumericKeypadModal from "./POSNumericKeypadModal";
import { shiftActions } from "../../sagas/shiftSlice";
import { useDispatch, useSelector } from "react-redux";
import { formatPrice } from "../../helpers/utility";
import "./CloseShiftModal.css";

const CloseShiftModal = ({
  isOpen,
  toggle,
  currentShift,
  onCloseShift,
  isSubmitting = false,
}) => {
  const [closingCash, setClosingCash] = useState(0);
  const [closingCard, setClosingCard] = useState(0);
  const [notes, setNotes] = useState("");
  const { summary } = useSelector((state) => state.shift);
  const [numPadOpen, setNumPadOpen] = useState(false);
  const [keypadTarget, setKeypadTarget] = useState("cash");
  const dispatch = useDispatch();

  const expectedCashAmount = Number(summary?.expectedCashAmount || 0);
  const expectedCardAmount = Number(summary?.expectedCardAmount || 0);
  const totalRetours = Number(summary?.totalRetours ?? summary?.totalReturnsAmount ?? 0);
  const totalCashPayments = Number(summary?.totalCashPayments || 0);
  const totalCardPayments = Number(summary?.totalCardPayments || 0);
  const cashReturns = Number(summary?.totalCashReturns ?? summary?.totalCashReturnsWithoutTicket ?? 0);
  const cardReturns = Number(summary?.totalCardReturns ?? summary?.totalCardReturnsWithoutTicket ?? 0);
  const totalRefunds = cashReturns + cardReturns;

  const cashDifference = useMemo(
    () => Number(closingCash || 0) - expectedCashAmount,
    [closingCash, expectedCashAmount]
  );
  const cardDifference = useMemo(
    () => Number(closingCard || 0) - expectedCardAmount,
    [closingCard, expectedCardAmount]
  );

  useEffect(() => {
    if (!isOpen || !currentShift?.id) return;
    setClosingCash(0);
    setClosingCard(0);
    setNotes("");
    dispatch(shiftActions.fetchSummary({ shiftId: currentShift.id }));
  }, [dispatch, isOpen, currentShift?.id]);

  useEffect(() => {
    if (!isOpen || !summary) return;
    setClosingCash(Number(summary?.closingAmountCash ?? expectedCashAmount));
    setClosingCard(Number(summary?.closingAmountCard ?? expectedCardAmount));
  }, [isOpen, summary, expectedCashAmount, expectedCardAmount]);

  const preventStepChangeKeys = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
  };

  const preventWheelStepChange = (e) => {
    e.currentTarget.blur();
  };

  const differenceColor = (value) => {
    if (value === 0) return "success";
    return value > 0 ? "warning" : "danger";
  };

  const handleConfirmCloseShift = () => {
    Swal.fire({
      title: "Confirmation de clôture",
      html: `
      <p>Êtes-vous sûr de vouloir <strong>clôturer ce shift</strong> ?</p>
      <hr/>
      <p><strong>Espèces :</strong> ${formatPrice(closingCash)}</p>
      <p><strong>Carte bancaire :</strong> ${formatPrice(closingCard)}</p>
    `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Oui, clôturer",
      cancelButtonText: "Annuler",
    }).then((result) => {
      if (result.isConfirmed && !isSubmitting) {
        onCloseShift({
          closingCash,
          closingCard,
          notes,
        });
      }
    });
  };

  const PaymentCard = ({
    type,
    title,
    icon,
    expected,
    closing,
    difference,
    payments,
    returns,
    opening,
  }) => (
    <div className="close-shift-payment-card">
      <div className="close-shift-card-head">
        <div className={`close-shift-icon ${type}`}>{icon}</div>
        <div>
          <div className="close-shift-card-title">{title}</div>
          <div className="close-shift-card-subtitle">Montant attendu</div>
          <div className="close-shift-card-note">Après retours client déduits</div>
        </div>
        <Badge color={differenceColor(difference)} className="close-shift-diff-badge">
          {formatPrice(difference)}
        </Badge>
      </div>

      <div className="close-shift-expected">{formatPrice(expected)}</div>

      <div className="close-shift-formula">
        <span>+ Fond d’ouverture {formatPrice(opening, false)}</span>
        <span>+ Encaissements {formatPrice(payments, false)}</span>
        {returns > 0 ? <span>- Remboursements {formatPrice(returns, false)}</span> : null}
      </div>

      <FormGroup className="mb-0 mt-3">
        <Label className="close-shift-input-label">Montant clôturé</Label>
        <Input
          id={type === "cash" ? "closingCash" : "closingCard"}
          type="text"
          readOnly
          value={closing}
          onKeyDown={preventStepChangeKeys}
          onWheel={preventWheelStepChange}
          onClick={() => {
            setKeypadTarget(type);
            setNumPadOpen(true);
          }}
          className="close-shift-amount-input"
          placeholder="Saisir le montant"
        />
      </FormGroup>
    </div>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg" className="close-shift-modal">
      <ModalHeader toggle={toggle}>
        <div>
          <div className="close-shift-title">Clôture du shift</div>
          <div className="close-shift-code">#{currentShift?.shiftCode}</div>
        </div>
      </ModalHeader>

      <ModalBody>
        <div className="close-shift-summary">
          <div className="close-shift-summary-item">
            <FaReceipt className="text-primary" />
            <div>
              <span>Retours clients</span>
              <strong>{formatPrice(totalRetours)}</strong>
            </div>
          </div>
          <div className="close-shift-summary-item">
            <FaUndoAlt className="text-danger" />
            <div>
              <span>Remboursements déduits</span>
              <strong>{formatPrice(totalRefunds)}</strong>
            </div>
          </div>
        </div>

        <Row className="g-3 mb-3">
          <Col md={6}>
            <PaymentCard
              type="cash"
              title="Espèces"
              icon={<FaMoneyBillWave />}
              expected={expectedCashAmount}
              closing={closingCash}
              difference={cashDifference}
              payments={totalCashPayments}
              returns={cashReturns}
              opening={Number(summary?.openingAmountCash || 0)}
            />
          </Col>
          <Col md={6}>
            <PaymentCard
              type="card"
              title="Carte bancaire"
              icon={<FaCreditCard />}
              expected={expectedCardAmount}
              closing={closingCard}
              difference={cardDifference}
              payments={totalCardPayments}
              returns={cardReturns}
              opening={Number(summary?.openingAmountCard || 0)}
            />
          </Col>
        </Row>

        <FormGroup className="mb-0">
          <Label for="closeShiftNotes" className="close-shift-input-label">
            Notes
          </Label>
          <Input
            id="closeShiftNotes"
            type="textarea"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ajouter une remarque (optionnel)"
            className="close-shift-notes"
          />
        </FormGroup>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isSubmitting}>
          Annuler
        </Button>

        <Button color="danger" onClick={handleConfirmCloseShift} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Clôture en cours...
            </>
          ) : (
            "Clôturer le shift"
          )}
        </Button>
      </ModalFooter>

      <POSNumericKeypadModal
        isOpen={numPadOpen}
        toggle={() => setNumPadOpen(false)}
        title={keypadTarget === "cash" ? "Total Espèces" : "Total Carte bancaire"}
        value={keypadTarget === "cash" ? closingCash : closingCard}
        allowDecimal={true}
        maxLength={10}
        onConfirm={(val) => {
          const amount = Number(val || 0);
          if (Number.isNaN(amount)) return;
          if (keypadTarget === "cash") setClosingCash(amount);
          if (keypadTarget === "card") setClosingCard(amount);
        }}
      />
    </Modal>
  );
};

export default CloseShiftModal;
